export type DocAudience = 'shared' | 'fixed' | 'free-form';

export type DocNavItem = {
  id: string;
  title: string;
  file: string;
  audience: DocAudience;
};

export type DocNavSection = {
  title: string;
  items: DocNavItem[];
};

const ALL_DOC_NAV_SECTIONS: DocNavSection[] = [
  {
    title: 'Start here',
    items: [
      {
        id: 'getting-started-fixed',
        title: 'Getting started',
        file: 'getting-started-fixed.md',
        audience: 'fixed',
      },
      {
        id: 'getting-started-free-form',
        title: 'Getting started',
        file: 'getting-started-free-form.md',
        audience: 'free-form',
      },
      {
        id: 'banner-types',
        title: 'Banner types',
        file: 'banner-types.md',
        audience: 'fixed',
      },
    ],
  },
  {
    title: 'Canvas setup',
    items: [
      {
        id: 'templates-and-backgrounds',
        title: 'Templates & backgrounds',
        file: 'templates-and-backgrounds.md',
        audience: 'fixed',
      },
      {
        id: 'free-form-frame',
        title: 'Free-form frame size',
        file: 'free-form-frame.md',
        audience: 'free-form',
      },
    ],
  },
  {
    title: 'Design',
    items: [
      { id: 'draw-tab', title: 'Draw tab', file: 'draw-tab.md', audience: 'shared' },
      { id: 'image-tab', title: 'Image tab', file: 'image-tab.md', audience: 'shared' },
      {
        id: 'adjust-and-effects',
        title: 'Adjust & Effects',
        file: 'adjust-and-effects.md',
        audience: 'shared',
      },
      {
        id: 'layers-and-canvas',
        title: 'Layers & canvas',
        file: 'layers-and-canvas.md',
        audience: 'shared',
      },
    ],
  },
  {
    title: 'Finish',
    items: [
      {
        id: 'topbar-and-theme-fixed',
        title: 'Top bar & theme',
        file: 'topbar-and-theme-fixed.md',
        audience: 'fixed',
      },
      {
        id: 'topbar-and-theme-free-form',
        title: 'Top bar & theme',
        file: 'topbar-and-theme-free-form.md',
        audience: 'free-form',
      },
      {
        id: 'save-and-export',
        title: 'Save & export',
        file: 'save-and-export.md',
        audience: 'shared',
      },
      {
        id: 'keyboard-shortcuts',
        title: 'Keyboard shortcuts',
        file: 'keyboard-shortcuts.md',
        audience: 'shared',
      },
      {
        id: 'tips-and-faq-fixed',
        title: 'Tips & FAQ',
        file: 'tips-and-faq-fixed.md',
        audience: 'fixed',
      },
      {
        id: 'tips-and-faq-free-form',
        title: 'Tips & FAQ',
        file: 'tips-and-faq-free-form.md',
        audience: 'free-form',
      },
    ],
  },
];

const itemMatchesAudience = (item: DocNavItem, isFreeForm: boolean): boolean => {
  if (item.audience === 'shared') {
    return true;
  }
  return isFreeForm ? item.audience === 'free-form' : item.audience === 'fixed';
};

export const getDocNavSections = (isFreeForm: boolean): DocNavSection[] =>
  ALL_DOC_NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => itemMatchesAudience(item, isFreeForm)),
  })).filter((section) => section.items.length > 0);

export const getDocNavFlat = (isFreeForm: boolean): DocNavItem[] =>
  getDocNavSections(isFreeForm).flatMap((section) => section.items);

export const getDefaultDocPageId = (isFreeForm: boolean): string =>
  isFreeForm ? 'getting-started-free-form' : 'getting-started-fixed';

export const getDocItemById = (
  id: string,
  isFreeForm: boolean,
): DocNavItem | undefined => getDocNavFlat(isFreeForm).find((item) => item.id === id);

/** @deprecated Use getDocNavSections(isFreeForm) — full nav includes both modes */
export const DOC_NAV_SECTIONS = ALL_DOC_NAV_SECTIONS;

export const DEFAULT_DOC_PAGE_ID = 'getting-started-fixed';
